import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { CodeoSkill } from '../types';
import { check, tweenCalls } from '../skillUsage';

/** A scale below 1 that the tween goes to: a literal like `.32`, or a named constant. */
const SHRINKS = /\bscale:\s*(?:0?\.\d*[1-9]|[A-Za-z_$(])/;

export const titleSettlesToLabelSkill: CodeoSkill = {
    name: SKILL_NAMES.titleSettlesToLabel,
    kind: 'codeo',
    description:
        'The headline that opens a graphic shrinks and travels into a corner, where it stays as ' +
        'the label for everything that follows — the title changes role instead of leaving, so ' +
        'the frame keeps saying what it is about while the detail arrives in the space it gave up.',
    checkLooksFor:
        'an element scaled down by a tween — the title shrinking into its corner label, rather than fading out while a small copy appears there',
    // Only the shrink is read. The move into the corner is as often a wrapper's tween beside the
    // title's own, or no move at all for a title already sitting in its corner, and whether the
    // label later dims or leaves with its subject is the author's call. A small copy popping in is
    // written as an entrance (`from`), so the fake this skill replaces still fails.
    documentUsesSkill: check((script) =>
        tweenCalls(script).some(({ method, chunk }) => method === 'to' && SHRINKS.test(chunk)),
    ),
    instructions: `# Title Settles To Label

A graphic about one subject opens on its title, big, as the hero of the frame. Once it has been read, the title doesn't leave: it shrinks and travels in one move up into a corner, where it stays for the rest of the scene as the label on everything after it — "Q3 results" becoming the tag above the figures, a product name becoming the header over its features. The detail arrives in the space the title just gave up. Things changing role instead of disappearing is what makes a sequence of facts read as one piece rather than a slideshow.

It is one element doing two jobs, so there is no swap to hide: no crossfade, no second copy of the words. Where the next graphic should open on the label already in its corner, declare that seam a handoff with \`${TOOL_NAMES.writeDesignSystem}\` so it is carried across.

## Markup

The title's place in the layout is the corner the label ends in: the top of a frame-filling column, on the margin. The detail is its own layer, laid out where it reads. The hero is the title displaced from that place — dropped down the frame at full size — so settling is a move back up and a scale down from its top-left corner, and nothing has to be measured. What moves is a wrapper around the title, so anything that belongs to the label — a rule, a pill — goes inside it and travels with it: a scaled element keeps its full-size layout box, and something laid out after it on its own would be left where the hero's bottom was.

\`\`\`html
<div class="absolute inset-0 flex flex-col items-start p-24">
  <div id="title-group" style="transform-origin: 0 0">
    <h1 id="title"
      style="margin: 0; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-size: 168px; font-weight: 800; line-height: 1; color: var(--l5-color-0, #101820)">Q3 results</h1>
  </div>
</div>
<div class="absolute inset-0 flex flex-col justify-center items-start gap-10 p-24">
  <div id="detail-1" style="font-family: var(--l5-font-1), sans-serif; font-size: 64px; color: var(--l5-color-0, #101820)">Revenue up 18%</div>
  <div id="detail-2" style="font-family: var(--l5-font-1), sans-serif; font-size: 64px; color: var(--l5-color-0, #101820)">Margin at a five-year high</div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const HERO_DROP = 340;
const LABEL_SCALE = 52 / 168; // --ds-type-label ÷ the title's font-size
const SETTLE_AT = 1.7, SETTLE = 0.8;
// The hero pose: the title dropped down the frame at full size, rising in as it fades up.
tl.fromTo('#title-group', { y: HERO_DROP + 60, opacity: 0 },
    { y: HERO_DROP, opacity: 1, duration: 0.6, ease: 'power3.out' }, 0.1);
// One move: back up into its corner and down to label size, from its top-left corner.
tl.to('#title-group', { y: 0, scale: LABEL_SCALE, duration: SETTLE, ease: 'power3.inOut' }, SETTLE_AT);
tl.from('#detail-1', { y: 40, opacity: 0, duration: 0.6, ease: 'power3.out' }, SETTLE_AT + SETTLE - 0.25);
tl.from('#detail-2', { y: 40, opacity: 0, duration: 0.6, ease: 'power3.out' }, SETTLE_AT + SETTLE + 0.35);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Hold the title first** — long enough to read it (about a second for a short title) before it settles.
- **Label size**: pick \`LABEL_SCALE\` so \`font-size × scale\` is the design system's label size, \`--ds-type-label\` — the smallest type the video reads at, below the detail the label heads. Any bigger and the label competes with what it heads; any smaller and nobody can read it.
- **Ease**: \`power3.inOut\` or \`expo.inOut\` — the settle is a deliberate move, so it accelerates away and lands softly. No overshoot: a label wobbling into its corner draws the eye back from the detail.
- **Overlap the detail with the settle**: start it about 0.25s before the label lands, as the example's \`SETTLE_AT + SETTLE - 0.25\` does. A detail that waits for the label to come to rest turns one rearrangement into two steps.
- **A centred title** settles to a centred label instead: centre the column (\`items-center\`) and set \`transform-origin: 50% 0\`.
- **Give it a tag** where the look calls for one: a rule or pill inside the title's wrapper that draws in (\`scaleX\` from 0) as it lands makes the role change explicit.
- **Across graphics**: keep the label in the same corner, at the same size, for as long as the subject holds, and only then let it go — with the subject, not before.

## Pitfalls

- **Never fade the title out and set a small copy in the corner.** That is a cut with two elements; the viewer sees the label appear, not the title become it.
- **No \`fontSize\`, \`width\` or \`height\` tweens** — uniform \`scale\` from a fixed \`transform-origin\`. Re-setting type every frame reflows the line and jitters.
- **\`white-space: nowrap\`** on the title, so a scaled title never wraps differently from the label it becomes.
- Keep no CSS \`transform\` on the wrapper: its layout is where the label rests, and the timeline carries the hero pose.
- Leave the corner empty until the label lands there — nothing else can claim the space it is travelling to.`,
};
